import { useCallback, useEffect, useRef, useState } from "react";
import QRCodeStyling from "qr-code-styling";
import { CloseCircleFilled, DeleteOutlined, DownloadOutlined } from "@ant-design/icons";
import NoDataPlaceholder from "./common/NoDataPlaceholder";
import ErrorPlaceholder from "./common/ErrorPlaceholder";
import { useNote, TheNote, TheNoteToggleButton } from "./common/TheNote";
import { ItemSizeType } from "./elements/SizeSelect";

interface RenderOneQRProps {
  data: string;
  index: number;
  itemSize?: ItemSizeType;
  onDataChange: (index: number, value: string) => void;
  onClear: () => void;
  showRemove: boolean;
  onRemove: () => void;
}

function RenderOneQR({
  data,
  index,
  itemSize,
  onDataChange,
  onClear,
  showRemove,
  onRemove,
}: RenderOneQRProps) {
  const [qrInstance, setQrInstance] = useState<QRCodeStyling | null>(null);
  const [debouncedData, setDebouncedData] = useState(data);
  const [hasError, setHasError] = useState(false);
  const { showNote, setShowNote, note, setNote } = useNote("qr", index);
  const containerRef = useRef<HTMLDivElement>(null);
  const qrInstanceRef = useRef<QRCodeStyling | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce data changes
  useEffect(() => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => setDebouncedData(data), 300);
    return () => { if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current); };
  }, [data]);

  // Create QR instance when debounced data changes
  useEffect(() => {
    if (qrInstanceRef.current) {
      try {
        if (containerRef.current) containerRef.current.innerHTML = "";
      } catch (error) {
        console.error("Error cleaning up previous QR instance:", error);
        if (containerRef.current) {
          try { containerRef.current.innerHTML = ""; } catch (clearError) {
            console.error("Error force clearing container:", clearError);
          }
        }
      }
    }

    if (containerRef.current) {
      try {
        if (!debouncedData || !String(debouncedData).trim()) {
          // Placeholder is handled in JSX
          containerRef.current.innerHTML = "";
        } else {
          containerRef.current.innerHTML = "";
        }
      } catch (error) {
        console.error("Error handling container:", error);
      }
    }

    if (debouncedData && String(debouncedData).trim()) {
      try {
        const trimmedData = String(debouncedData).trim();
        const newInstance = new QRCodeStyling({
          data: trimmedData,
          width: 300,
          height: 300,
          margin: 0,
          dotsOptions: { type: "square", color: "#000000" },
          cornersSquareOptions: { type: "square", color: "#000000" },
          cornersDotOptions: { type: "square", color: "#000000" },
          backgroundOptions: { color: "#ffffff" },
        });
        setQrInstance(newInstance);
        qrInstanceRef.current = newInstance;
        setHasError(false);
      } catch (error) {
        console.error(`Error creating QR for data ${index}:`, error);
        setQrInstance(null);
        qrInstanceRef.current = null;
        setHasError(true);
        if (containerRef.current) {
          try { containerRef.current.innerHTML = ""; } catch (clearError) {
            console.error("Error clearing container after QR creation failure:", clearError);
          }
        }
      }
    } else {
      setQrInstance(null);
      qrInstanceRef.current = null;
      setHasError(false);
    }
  }, [debouncedData, index]);

  // Render QR code
  useEffect(() => {
    if (qrInstance && containerRef.current) {
      const timeoutId = setTimeout(() => {
        try {
          if (containerRef.current) containerRef.current.innerHTML = "";
          const qrWrapper = document.createElement("div");
          qrWrapper.style.width = "100%";
          qrWrapper.style.height = "auto";
          qrWrapper.style.display = "flex";
          qrWrapper.style.justifyContent = "center";
          qrWrapper.style.alignItems = "center";
          if (containerRef.current) {
            containerRef.current.appendChild(qrWrapper);
            qrInstance.append(qrWrapper);
          }
        } catch (error) {
          console.error(`Error rendering QR ${index}:`, error);
          if (containerRef.current) {
            try { containerRef.current.innerHTML = ""; } catch (clearError) {
              console.error("Error clearing container after render failure:", clearError);
            }
          }
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    } else if (!qrInstance && containerRef.current) {
      try {
        // Placeholder is handled in JSX
        containerRef.current.innerHTML = "";
      } catch (error) {
        console.error("Error showing placeholder:", error);
      }
    }
  }, [qrInstance, index]);

  // Cleanup on unmount
  useEffect(() => {
    const container = containerRef.current;
    return () => {
      if (container) {
        try { container.innerHTML = ""; } catch (error) { /* ignore */ }
      }
    };
  }, []);

  const handleInputClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => { (e.target as HTMLInputElement).select(); }, []);

  const handleClearClick = useCallback(() => {
    onClear();
    setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 10);
  }, [onClear]);

  const handleDownloadPNG = useCallback(() => {
    if (qrInstance) {
      qrInstance.download({ name: `qr-code-${index + 1}`, extension: "png" });
    }
  }, [qrInstance, index]);

  return (
    <div className={`qr-preview size-${itemSize || "medium"} p-4 relative card h-100`}>
      {/* Note */}
      <TheNote showNote={showNote} note={note} setNote={setNote} />

      {/* Input row: [input + clear X] */}
      <div style={{ position: "relative" }}>
        <input
          ref={inputRef}
          type="text"
          className="form-control highlight"
          value={data}
          name={"qrData" + index}
          onChange={(e) => onDataChange(index, e.target.value)}
          onClick={handleInputClick}
          placeholder="QR data..."
          style={{
            borderWidth: "2px",
            paddingTop: "2px",
            paddingBottom: "2px",
            paddingRight: data ? "26px" : undefined,
          }}
        />
        {data && (
          <button
            type="button"
            onClick={handleClearClick}
            title="Clear input"
            style={{
              position: "absolute",
              right: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              padding: "2px",
              cursor: "pointer",
              color: "#bbb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
              transition: "color 0.2s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#666")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#bbb")}
          >
            <CloseCircleFilled style={{ fontSize: "15px" }} />
          </button>
        )}
      </div>

      {/* DISPLAY */}
      <div
        className="qr-display w-100"
        style={{
          paddingBottom: "100%",
          border: "2px solid #c5c9cc",
          borderRadius: "12px",
          position: "relative",
          overflow: "hidden"
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "10px",
          }}
        >
          {(!debouncedData || !String(debouncedData).trim()) && !hasError && (
            <NoDataPlaceholder
              onClick={() => { if (inputRef.current) inputRef.current.focus(); }}
              onMouseEnter={() => { if (inputRef.current) inputRef.current.placeholder = "Enter data here!"; }}
            />
          )}
          {hasError && (
            <ErrorPlaceholder
              message="Cannot encode this QR data"
              onClick={() => { if (inputRef.current) inputRef.current.focus(); }}
              onMouseEnter={() => { if (inputRef.current) inputRef.current.placeholder = "Enter data here!"; }}
            />
          )}
          <div
            ref={containerRef}
            style={{
              width: "100%",
              height: "100%",
              display: (!debouncedData || !String(debouncedData).trim() || hasError) ? "none" : "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          ></div>
        </div>
      </div>

      {/* Actions */}
      <div className="w-100" style={{ display: "flex", gap: "15px", justifyContent: "space-between" }}>
        <button
          type="button"
          className="btn btn-outline-danger btn-sm rounded-pill d-flex align-items-center justify-content-center p-0"
          style={{ width: "36px", height: "36px", flexShrink: 0 }}
          onClick={onRemove}
          title="Remove QR"
          disabled={!showRemove}
        >
          <DeleteOutlined style={{ fontSize: "16px" }} />
        </button>
        <TheNoteToggleButton showNote={showNote} setShowNote={setShowNote} />
        <button
          type="button"
          className="btn btn-success btn-sm rounded-pill d-flex align-items-center justify-content-center p-0"
          style={{ width: "36px", height: "36px", flexShrink: 0 }}
          onClick={handleDownloadPNG}
          title="Download PNG"
          disabled={!qrInstance}
        >
          <DownloadOutlined style={{ fontSize: "16px" }} />
        </button>
      </div>
    </div>
  );
}

export default RenderOneQR;
